import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faTruckFast,
  faTruck,
  faUser,
  faLocationDot,
  faClipboardList,
  faShoppingCart,
  faFileInvoice,
  faSmile,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { ArrowLeft, ChevronDown, ChevronUp, Truck } from "lucide-react";

import Bluedart from "../../../assets/bluedart.png";
import Delehivery from "../../../assets/delehivery.png";
import EcomExpress from "../../../assets/ecom-expresss.avif";
import Shadowfax from "../../../assets/shadowfax.png";
import Xpressbees from "../../../assets/xpressbees.png";
import Shiprocket from "../../../assets/shiprocket.webp";
import NimbusPost from "../../../assets/nimbuspost.webp";
import ShreeMaruti from "../../../assets/shreemaruti.png";
import DTDC from "../../../assets/dtdc.png";
import Amazon from "../../../assets/amazon.jpg";

import {
  MapPin,
  Package,
  Clock,
  Box,
  Phone,
  User as LucideUser,
  Home,
  Hash,
  Layers,
  Scale,
  List,
  Tag,
  IndianRupee,
  CreditCard,
} from "lucide-react";

const THEME_GREEN = "#0CBB7D";

/* ---------------------------
   formatting helpers
   --------------------------- */
const capitalizeWord = (w) => {
  if (!w || typeof w !== "string") return "";
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
};
const titleCase = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((s) => (s ? capitalizeWord(s) : s))
    .join(" ");
};
const capitalizeAfterComma = (input) => {
  if (!input) return "";
  return input
    .split(",")
    .map((chunk) => titleCase(chunk.trim()))
    .filter(Boolean)
    .join(", ");
};
const formatAddressFull = (address = "") => {
  if (!address) return "";
  const parts = address.split(",");
  parts[0] = titleCase(parts[0].trim());
  for (let i = 1; i < parts.length; i++) parts[i] = titleCase(parts[i].trim());
  return parts
    .map((p) => p)
    .filter(Boolean)
    .join(", ");
};

/* ---------------------------
   PersonBlock
   --------------------------- */
function PersonBlock({ data = {}, side }) {
  const rawAddress = data?.address || "";
  const mergedAddressParts = [];
  if (rawAddress) mergedAddressParts.push(formatAddressFull(rawAddress));
  if (data?.city) mergedAddressParts.push(titleCase(String(data.city)));
  if (data?.state) mergedAddressParts.push(titleCase(String(data.state)));
  const mergedAddress = mergedAddressParts.join(", ");

  return (
    <div className="flex-1 min-w-0 bg-white p-2 md:p-4 rounded-xl shadow-sm border border-[#e6f8f3]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded bg-[#f0fdfa]">
            {side === "from" ? (
              <Truck size={18} color={THEME_GREEN} />
            ) : (
              <MapPin size={18} color={THEME_GREEN} />
            )}
          </div>
          <div>
            <div className="text-[13px] font-semibold text-gray-600">
              {side === "from" ? "From" : "To"}
            </div>
            <div className="text-[11px] text-gray-400">
              {side === "from" ? "Pickup Address" : "Delivery Details"}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-1 text-sm">
        {/* Contact Name */}
        <div className="flex items-center py-3 border-t first:border-t-0">
          <div className="w-40 pr-3 flex items-center gap-2 text-[12px] text-gray-500">
            <LucideUser size={16} />
            <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">
              Contact Name
            </div>
          </div>
          <div className="flex-1 pl-3 border-l border-[#eef8f4]">
            <div className="text-[14px] text-gray-800 font-medium">
              {data?.contactName
                ? capitalizeAfterComma(String(data.contactName))
                : "--"}
            </div>
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-center py-2 border-t">
          <div className="w-40 pr-3 flex items-center gap-2 text-[12px] text-gray-500">
            <Phone size={16} />
            <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">
              Phone
            </div>
          </div>
          <div className="flex-1 pl-3 border-l border-[#eef8f4]">
            <div className="text-[14px] text-gray-800">
              {data?.phoneNumber || "--"}
            </div>
          </div>
        </div>

        {/* Email */}
        <div className="flex items-center py-2 border-t">
          <div className="w-40 pr-3 flex items-center gap-2 text-[12px] text-gray-500">
            <Layers size={16} />
            <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">
              Email
            </div>
          </div>
          <div className="flex-1 pl-3 border-l border-[#eef8f4]">
            <div className="text-[14px] text-gray-800">
              {data?.email || "--"}
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="flex items-start py-2 border-t">
          <div className="w-40 pr-3 flex items-start gap-2 text-[12px] text-gray-500">
            <Home size={16} />
            <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">
              Address
            </div>
          </div>
          <div className="flex-1 pl-3 border-l border-[#eef8f4]">
            <div className="text-[14px] text-gray-800">
              {mergedAddress || "--"}
            </div>
          </div>
        </div>

        {/* Pincode */}
        <div className="flex items-center py-2 border-t">
          <div className="w-40 pr-3 flex items-center gap-2 text-[12px] text-gray-500">
            <Hash size={16} />
            <div className="uppercase tracking-wider text-[11px] text-gray-400 font-medium">
              Pincode
            </div>
          </div>
          <div className="flex-1 pl-3 border-l border-[#eef8f4]">
            <div className="text-[14px] text-gray-800">
              {data?.pinCode || "--"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------
   Main Tracing2 component
   --------------------------- */
function Tracing2() {
  const { awb } = useParams();
  const navigate = useNavigate();
  const [trackingAwb, setTrackingAwb] = useState(null);
  const [tracking, setTracking] = useState([]);
  const [feedback, setFeedback] = useState(null); // ✅ Fix 4: emoji feedback
  const [submitted, setSubmitted] = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const THEME_GREEN = "#0CBB7D";
  const [timelineOpen, setTimelineOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );

  const [pickupDeliveryOpen, setPickupDeliveryOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );

  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [awbInput, setAwbInput] = useState("");

const handleTrackNow = async () => {
  if (!awbInput.trim()) return; // validation
  try {
    const [session, token] = document.cookie.split("=");
    const response = await axios.get(
      `${REACT_APP_BACKEND_URL}/orders/GetTrackingByAwb/${awbInput.trim()}`,
      { headers: { authorization: `Bearer ${token}` } }
    );
    if (response.status === 200) {
      setTrackingAwb(response.data);
      const filtered = Array.isArray(response.data.tracking)
        ? response.data.tracking.filter(
            (i) => i && (i.Instructions || i.StatusDateTime || i.StatusLocation)
          )
        : [];
      setTracking(filtered.length ? filtered : dummyTracking);
    } else {
      setTrackingAwb(null);
      setTracking([]);
    }
  } catch (error) {
    console.error("Tracking fetch error:", error);
    setTrackingAwb(null);
    setTracking([]);
  }
};


  const carrierLogos = {
    Bluedart,
    Delhivery: Delehivery,
    EcomExpress,
    Shadowfax,
    Xpressbees,
    NimbusPost,
    Shiprocket,
    ShreeMaruti,
    DTDC,
    Amazon,
  };

  const statusSteps = [
    { label: "Ready to ship", icon: faBoxOpen },
    { label: "In transit", icon: faTruckFast },
    { label: "Out for delivery", icon: faTruck },
    { label: "Delivered", icon: faUser },
  ];

  const statusMapping = {
    "Ready To Ship": 0,
    "In-transit": 1,
    Undelivered: 1,
    RTO: 1,
    "RTO In-transit": 1,
    "Out for Delivery": 2,
    "RTO Out for Delivery": 2,
    Delivered: 3,
    "RTO Delivered": 3,
  };

  const dummyTracking = [
    {
      Instructions: "Order picked up from seller",
      StatusDateTime: new Date(
        Date.now() - 1000 * 60 * 60 * 24 * 3
      ).toISOString(),
      StatusLocation: "Bangalore, KA",
    },
    {
      Instructions: "Arrived at sorting center",
      StatusDateTime: new Date(
        Date.now() - 1000 * 60 * 60 * 24 * 2
      ).toISOString(),
      StatusLocation: "Bangalore Hub",
    },
    {
      Instructions: "In transit to destination",
      StatusDateTime: new Date(
        Date.now() - 1000 * 60 * 60 * 24 * 1
      ).toISOString(),
      StatusLocation: "En route",
    },
    {
      Instructions: "Out for delivery",
      StatusDateTime: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
      StatusLocation: "Chennai, TN",
    },
    {
      Instructions: "Delivered",
      StatusDateTime: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
      StatusLocation: "Chennai, TN",
    },
  ];

  const currentStep = useMemo(
    () => statusMapping[trackingAwb?.status] ?? 0,
    [trackingAwb]
  );

  useEffect(() => {
    // Set initial state only once on mount
    if (window.innerWidth >= 768) {
      setPickupDeliveryOpen(true);
      setTimelineOpen(true);
    } else {
      setPickupDeliveryOpen(false);
      setTimelineOpen(false);
    }
    // No resize listener needed
  }, []);

  useEffect(() => {
    const trackingData = async () => {
      try {
        const [session, token] = document.cookie.split("=");
        const response = await axios.get(
          `${REACT_APP_BACKEND_URL}/orders/GetTrackingByAwb/${awb}`,
          { headers: { authorization: `Bearer ${token}` } }
        );
        console.log("Tracking response:", response);
        if (response.status === 200) {
          setTrackingAwb(response.data);
          const filtered = Array.isArray(response.data.tracking)
            ? response.data.tracking.filter(
                (i) =>
                  i && (i.Instructions || i.StatusDateTime || i.StatusLocation)
              )
            : [];
          setTracking(filtered.length ? filtered : dummyTracking);
        } else {
          setTrackingAwb(null);
          setTracking([]);
        }
      } catch (error) {
        console.error("Tracking fetch error:", error);
        setTrackingAwb(null);
        setTracking([]);
      }
    };
    trackingData();
  }, [awb, REACT_APP_BACKEND_URL]);

  const lastUpdateStr =
    trackingAwb && trackingAwb.updatedAt
      ? new Date(trackingAwb.updatedAt).toLocaleString()
      : "N/A";

  const products = Array.isArray(trackingAwb?.productDetails)
    ? trackingAwb.productDetails
    : [];

  const providerDisplayName =
    trackingAwb?.deliveryPartner?.name || "Courier Not Found";

  const providerLogo =
    trackingAwb?.deliveryPartner?.logo ||
    carrierLogos[trackingAwb?.provider] ||
    carrierLogos[trackingAwb?.courierServiceName] ||
    carrierLogos.Shiprocket;

  const timelineItems = tracking.length
    ? tracking.filter(
        (item) =>
          item &&
          (item.Instructions || item.StatusDateTime || item.StatusLocation)
      )
    : [];

  return (
    <div className="w-full min-h-screen bg-gray-50 sm:p-2">
      
      <div className="max-w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* EXTRA TRACKING COLUMN (Desktop only, leftmost) */}
        <div className="flex flex-col gap-3 lg:col-span-2 ">
          <div className="bg-white rounded-xl shadow-md border border-[#e6f8f3] p-3 md:p-6 flex flex-col items-start bg-gradient-to-r from-green-200 via-green-100 to-gray-200">
            <div className="text-[14px] font-semibold text-gray-800 mb-3">
              Track Another Order
            </div>
            <input
  type="text"
  className="w-full border border-[#0CBB7D] rounded-md px-3 py-2 text-xs text-gray-800"
  placeholder="Enter Waybill Number"
  value={awbInput}
  onChange={(e) => setAwbInput(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") handleTrackNow();
  }}
/>
<button
  className="w-full text-sm md:text-xs mt-4 bg-[#0CBB7D] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#099761] transition"
  onClick={handleTrackNow}
>
  Track Now
</button>

          </div>
        </div>

        {/* LEFT: 3/4 width */}
        <div className="lg:col-span-7 flex flex-col gap-3">
          {/* header */}

          {/* Top Status Bar with Gradient Background */}
          <div className="hidden sm:flex flex-col w-full rounded-xl shadow-lg p-6 bg-gradient-to-r from-green-200 via-green-100 to-gray-200">
            <div className="flex justify-between items-center w-full relative">
              {statusSteps.map((s, i) => {
                const active = i <= currentStep;
                return (
                  <div
                    key={i}
                    className="flex flex-col items-center flex-1 relative"
                  >
                    {/* Step Circle */}
                    <div
                      className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-semibold transition-all duration-300 ${
                        active
                          ? "bg-[#0CBB7D] text-white shadow-lg scale-105 z-10"
                          : "bg-gray-300 text-gray-600 z-10"
                      }`}
                    >
                      <FontAwesomeIcon icon={s.icon} />
                    </div>

                    {/* Label */}
                    <p
                      className={`mt-3 text-sm font-medium text-center ${
                        active ? "text-[#0CBB7D]" : "text-gray-500"
                      }`}
                    >
                      {s.label}
                    </p>

                    {/* Connector Line */}
                    {i < statusSteps.length - 1 && (
                      <div
                        className={`absolute top-7 left-1/2 w-full h-[3px] -translate-x-1.5/2 transition-colors duration-300 ${
                          i < currentStep ? "bg-[#0CBB7D]" : "bg-gray-400"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile connected line */}
          <div className="sm:hidden flex items-center justify-between relative rounded-xl shadow-lg p-3 bg-gradient-to-r from-green-200 via-green-100 to-gray-200">
            {statusSteps.map((s, i) => {
              const active = i <= currentStep;
              return (
                <div
                  key={i}
                  className="flex flex-col items-center flex-1 relative"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                      active
                        ? "bg-[#0CBB7D] text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    <FontAwesomeIcon icon={s.icon} />
                  </div>
                  <div className="text-[10px] mt-1 text-center">{s.label}</div>
                  {i < statusSteps.length - 1 && (
                    <div
                      className={`absolute top-4 left-1/2 h-[2px] w-full -translate-x-1.5/2 ${
                        i < currentStep ? "bg-[#0CBB7D]" : "bg-gray-300"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Courier + AWB + Last update */}
          <div className="bg-white rounded-lg shadow md:p-4 py-4  flex items-center justify-between bg-gradient-to-r from-green-100 via-gray-100 to-gray-100">
            <div className="flex items-center md:gap-3">
              <div className="md:w-14 md:h-14 w-12 h-12 rounded-md flex items-center justify-center pl-1">
                {providerLogo ? (
                  <img
                    src={providerLogo}
                    alt={providerDisplayName}
                    className="md:w-10 md:h-10 w-8 h-8 rounded-md object-contain"
                  />
                ) : (
                  <div className=" text-gray-700 font-semibold">
                    {(providerDisplayName || "C").charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <div className="md:text-[17px] text-[13px] font-semibold text-gray-800">
                  {providerDisplayName}
                </div>
                <div className="text-[11px] md:text-sm text-gray-600">
                  Waybill No.:{" "}
                  <span className="font-medium text-gray-700">
                    {trackingAwb?.awb_number ??
                      trackingAwb?.shipment_id ??
                      "--"}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right text-sm pr-4">
              <div className="text-[11px] md:text-xs text-gray-500">
                Last update
              </div>
              <div className="text-[11px] md:text-sm text-gray-700">
                {lastUpdateStr}
              </div>
            </div>
          </div>

          {/* Tracking Details - show after courier/awb on mobile */}
          <div className="block lg:hidden ">
            <div className="rounded-lg shadow overflow-hidden flex flex-col bg-gradient-to-r from-green-100 via-gray-100 to-gray-100">
              {/* Header */}
              <div className="flex items-center justify-between py-4 pl-3 border-b">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="text-[#0CBB7D]"
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-700">
                      Tracking Details
                    </div>
                    <div className="text-xs text-gray-400">
                      {timelineItems.length} events
                    </div>
                  </div>
                </div>
                {/* Expand / Collapse Button */}
                <button
                  onClick={() => setTimelineOpen((s) => !s)}
                  className="p-2 rounded hover:bg-gray-50 transition"
                  aria-expanded={timelineOpen}
                  title={timelineOpen ? "Collapse" : "Expand"}
                >
                  {timelineOpen ? <ChevronUp /> : <ChevronDown />}
                </button>
              </div>
              {/* Timeline Content */}
              <AnimatePresence initial={false}>
                {timelineOpen && (
                  <motion.div
                    key="timeline"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.4, ease: [0.3, 0.2, 0.1, 0] },
                      opacity: { duration: 0.25 },
                    }}
                    className="p-4 overflow-y-auto"
                    style={{ willChange: "height, opacity" }}
                  >
                    {timelineItems.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <FontAwesomeIcon
                          icon={faCircleExclamation}
                          className="text-2xl mb-2"
                        />
                        <span className="text-sm">
                          Tracking details not available
                        </span>
                      </div>
                    ) : (
                      <ol className="relative mx-auto max-w-3xl">
                        {/* Center Vertical Line */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-500 -translate-x-1/2" />

                        {timelineItems.map((item, idx) => {
                          const dt = item.StatusDateTime
                            ? new Date(item.StatusDateTime)
                            : null;
                          const isLeft = idx % 2 !== 0; // odd → left, even → right

                          return (
                            <li
                              key={idx}
                              className="mb-10 relative w-full flex"
                            >
                              {/* Timeline Dot */}
                              <span className="absolute left-1/2 w-2 h-2 bg-[#0CBB7D] rounded-full -translate-x-1/2 z-10" />

                              {/* Left Side */}
                              <div className="w-1/2 flex justify-end pr-4">
                                {isLeft && (
                                  <div className="text-right text-gray-700 text-sm font-normal max-w-xs">
                                    <div className="text-[10px] text-gray-400">
                                      {dt
                                        ? dt.toLocaleString([], {
                                            dateStyle: "short",
                                            timeStyle: "short",
                                          })
                                        : "N/A"}
                                    </div>
                                    <div className="text-[13px]">
                                      {item.Instructions || "Status updated"}
                                    </div>
                                    {item.StatusLocation && (
                                      <div className="text-[#0CBB7D] text-[11px] font-light">
                                        {item.StatusLocation}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Right Side */}
                              <div className="w-1/2 flex justify-start pl-4">
                                {!isLeft && (
                                  <div className="text-left text-gray-700 text-sm font-normal max-w-xs">
                                    <div className="text-[10px] text-gray-400">
                                      {dt
                                        ? dt.toLocaleString([], {
                                            dateStyle: "short",
                                            timeStyle: "short",
                                          })
                                        : "N/A"}
                                    </div>
                                    <div className="text-[13px]">
                                      {item.Instructions || "Status updated"}
                                    </div>
                                    {item.StatusLocation && (
                                      <div className="text-[#0CBB7D] text-[11px] font-light">
                                        {item.StatusLocation}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Pickup & Delivery with toggle */}
          <div
            className={`bg-gradient-to-r from-green-100 via-gray-100 to-gray-100 rounded-lg shadow pt-4 ${
              pickupDeliveryOpen ? "pl-3 md:p-4" : "pl-3 md:px-5"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="flex items-center gap-2 text-[14px] font-semibold text-gray-700">
                <FontAwesomeIcon
                  icon={faClipboardList}
                  className="text-[#0CBB7D]"
                />
                Pickup & Delivery
              </h3>
              <button
                onClick={() => setPickupDeliveryOpen((s) => !s)}
                className="p-2 rounded transition "
                aria-expanded={pickupDeliveryOpen}
                title={pickupDeliveryOpen ? "Collapse" : "Expand"}
              >
                {pickupDeliveryOpen ? <ChevronUp /> : <ChevronDown />}
              </button>
            </div>
            <AnimatePresence initial={false}>
              {pickupDeliveryOpen && (
                <motion.div
                  key="pickupDelivery"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PersonBlock
                      data={
                        trackingAwb?.pickupAddress || trackingAwb?.pickup || {}
                      }
                      side="from"
                    />
                    <PersonBlock
                      data={
                        trackingAwb?.receiverAddress ||
                        trackingAwb?.receiver ||
                        {}
                      }
                      side="to"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Products & Price */}
          <div className="bg-gradient-to-r from-green-100 via-gray-100 to-gray-100 rounded-lg shadow p-2 md:p-4">
            <h3 className="text-[14px] font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Package size={16} className="text-[#0CBB7D]" />
              Products & Price
            </h3>

            <div className="space-y-0 text-xs divide-y divide-gray-200 px-3">
              {products.length ? (
                products.map((p, idx) => (
                  <div key={idx} className="flex justify-between py-2">
                    <p className="text-gray-700 font-medium uppercase">
                      {p.name}
                    </p>
                    <p className="text-gray-600 font-semibold uppercase">
                      {p.quantity} × ₹{p.unitPrice}
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-gray-500">NO PRODUCT DETAILS</div>
              )}
            </div>
          </div>

          {/* Order Details */}
          <div className="bg-gradient-to-r from-green-100 via-gray-100 to-gray-100 rounded-lg shadow p-2 md:p-4">
            <h3 className="text-[14px] font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <List size={16} className="text-[#0CBB7D]" />
              Order Details
            </h3>

            <div className="grid md:grid-cols-2 sm:divide-y md:divide-y-0 md:divide-x md:divide-gray-200 text-sm">
              {/* Section 1 */}
              <div className="md:py-3 md:px-3 px-2">
                <div className="flex justify-between py-2">
                  <span className="text-xs text-gray-500 uppercase">
                    Order ID
                  </span>
                  <span className="text-gray-800 font-medium">
                    {trackingAwb?.orderId}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-xs text-gray-500 uppercase">
                    Placed On
                  </span>
                  <span className="text-gray-800 font-medium">
                    {trackingAwb?.shipmentCreatedAt
                      ? new Date(
                          trackingAwb?.shipmentCreatedAt
                        ).toLocaleString()
                      : "--"}
                  </span>
                </div>
              </div>

              {/* Section 2 */}
              <div className="md:py-3 md:px-3 px-2">
                <div className="flex justify-between py-2">
                  <span className="text-xs text-gray-500 uppercase">
                    Payment Mode
                  </span>
                  <span className="text-gray-800 font-medium">
                    {trackingAwb?.paymentDetails?.method ?? "--"}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-xs text-gray-500 uppercase">
                    Invoice Value
                  </span>
                  <span className="text-gray-800 font-medium">
                    ₹{trackingAwb?.paymentDetails?.amount ?? "--"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: 1/4 width */}
        <div className="lg:col-span-3 flex flex-col gap-3">
          {/* Tracking timeline expandable */}
          <div className="hidden lg:flex bg-gradient-to-r from-green-100 via-gray-100 to-gray-100 rounded-lg shadow overflow-hidden flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="text-[#0CBB7D]"
                />
                <div>
                  <div className="text-sm font-semibold text-gray-700">
                    Tracking Details
                  </div>
                  <div className="text-xs text-gray-400">
                    {timelineItems.length} events
                  </div>
                </div>
              </div>

              {/* Expand / Collapse Button */}
              <button
                onClick={() => setTimelineOpen((s) => !s)}
                className="p-2 rounded hover:bg-gray-50 transition"
                aria-expanded={timelineOpen}
                title={timelineOpen ? "Collapse" : "Expand"}
              >
                {timelineOpen ? <ChevronUp /> : <ChevronDown />}
              </button>
            </div>

            {/* Timeline Content */}
            <AnimatePresence initial={false}>
              {timelineOpen && (
                <motion.div
                  key="timeline"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: { duration: 0.4, ease: [0.3, 0.2, 0.1, 0] },
                    opacity: { duration: 0.25 },
                  }}
                  className="p-4 overflow-y-auto"
                  style={{ willChange: "height, opacity" }}
                >
                  {timelineItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <FontAwesomeIcon
                        icon={faCircleExclamation}
                        className="text-2xl mb-2"
                      />
                      <span className="text-sm">
                        Tracking details not available
                      </span>
                    </div>
                  ) : (
                    <ol className="relative mx-auto max-w-3xl">
                      {/* Center Vertical Line */}
                      <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-500 -translate-x-1/2" />

                      {timelineItems.map((item, idx) => {
                        const dt = item.StatusDateTime
                          ? new Date(item.StatusDateTime)
                          : null;
                        const isLeft = idx % 2 !== 0; // odd → left, even → right

                        return (
                          <li key={idx} className="mb-10 relative w-full flex">
                            {/* Timeline Dot */}
                            <span className="absolute left-1/2 w-2 h-2 bg-[#0CBB7D] rounded-full -translate-x-1/2 z-10" />

                            {/* Left Side */}
                            <div className="w-1/2 flex justify-end pr-4">
                              {isLeft && (
                                <div className="text-right text-gray-700 text-sm font-normal max-w-xs">
                                  <div className="text-[10px] text-gray-400">
                                    {dt
                                      ? dt.toLocaleString([], {
                                          dateStyle: "short",
                                          timeStyle: "short",
                                        })
                                      : "N/A"}
                                  </div>
                                  <div className="text-[13px]">
                                    {item.Instructions || "Status updated"}
                                  </div>
                                  {item.StatusLocation && (
                                    <div className="text-[#0CBB7D] text-[11px] font-light">
                                      {item.StatusLocation}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Right Side */}
                            <div className="w-1/2 flex justify-start pl-4">
                              {!isLeft && (
                                <div className="text-left text-gray-700 text-sm font-normal max-w-xs">
                                  <div className="text-[10px] text-gray-400">
                                    {dt
                                      ? dt.toLocaleString([], {
                                          dateStyle: "short",
                                          timeStyle: "short",
                                        })
                                      : "N/A"}
                                  </div>
                                  <div className="text-[13px]">
                                    {item.Instructions || "Status updated"}
                                  </div>
                                  {item.StatusLocation && (
                                    <div className="text-[#0CBB7D] text-[11px] font-light">
                                      {item.StatusLocation}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Feedback 5x5 grid */}
          <div className="bg-gradient-to-r from-green-100 via-gray-100 to-gray-100 rounded-lg shadow p-4 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} className="text-[#0CBB7D]" />
                <div>
                  <div className="text-sm font-semibold text-gray-700">
                    How was your Delivery?
                  </div>
                </div>
              </div>
            </div>

            {!feedbackSubmitted ? (
              <>
                <div className="flex justify-between items-center mb-4 text-2xl">
                  {["😡", "😕", "😐", "😊", "🤩"].map((emoji, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedEmoji(idx)}
                      className={`p-2 transition transform ${
                        selectedEmoji === idx
                          ? "scale-125 text-[#0CBB7D]"
                          : "text-gray-400 hover:scale-110"
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => {
                    if (selectedEmoji !== null) {
                      setFeedbackSubmitted(true);
                    }
                  }}
                  className="bg-[#0CBB7D] text-white rounded-md py-2 text-sm font-medium hover:bg-[#0aaa6f] transition"
                >
                  Submit Feedback
                </button>
              </>
            ) : (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="text-center text-3xl"
              >
                {["😡", "😕", "😐", "😊", "🤩"][selectedEmoji]}
                <div className="text-sm text-gray-600 mt-2">
                  Feedback submitted!
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tracing2;
